import { Col, Container, Row } from "reactstrap";

import Breadcrumbs from "@components/ui-components/breadcrumbs";
import Card from "@components/ui-components/card";
import Charts from "@components/charts";
import { LoaderOverlay } from "@components/spinner";
import { NoDataComponent } from "@components/no-data-component";
import { getColors } from "../misc/constants";
import { getPlural } from "@src/utility/helpers/utils";
import size from "lodash/size";
import uppercase from "lodash/upperCase";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import useResponse from "./use-response";
import { useTranslation } from "react-i18next";

const ScoreCircle = ({ score, type }) => {
    const color = getColors(type, score);
    const progressValue = type === "csat" ? score - 30 : (score + 100) / 2;
    return (
        <Charts
            type="pie"
            plotOptions={{
                pie: {
                    showInLegend: false,
                    dataLabels  : {
                        enabled: false
                    }
                }
            }}
            tooltip={{
                enabled: true
            }}
            events={{
                render () {
                    const chart = this;
                    const centerX = (chart.plotWidth / 2) + chart.plotLeft;
                    const centerY = (chart.plotHeight / 2) + chart.plotTop;
                    if (!chart.customLabel) {
                        chart.customLabel = chart.renderer
                            .text(`${progressValue}%`, centerX, centerY)
                            .attr({
                                align      : "center",
                                "font-size": "24px",
                                zIndex     : 5
                            })
                            .css({
                                color     : "#333",
                                fontSize  : "24px",
                                fontWeight: "bold"
                            })
                            .add();
                    } else {
                        chart.customLabel.attr({
                            text: `${progressValue}%`
                        });
                    }
                }
            }}
            series={[
                {
                    name: type,
                    data: [
                        {
                            name: "Score",
                            y   : progressValue,
                            color
                        },
                        {
                            name : "Remaining",
                            y    : 100 - progressValue, // 100 - progress %
                            color: "#e0e0e0" // light gray
                        }
                    ]
                }
            ]}/>
    );
};

// Combined component: Score + Stats
const SurveyStats = ({ type, stats }) => {

    const isCSAT = type === "csat";
    const labels = isCSAT ? ["Satisfied", "Neutral", "Unsatisfied"] : ["Promoter", "Passive", "Detractor"];
    const colors = ["success", "warning", "danger"];
    const total = useMemo(
        () => Object.values(stats).reduce((sum, val) => sum + val, 0),
        [stats]
    );
    const { t } = useTranslation();
    return (
        <Row className="align-items-center mt-4">
            <Col md={5}>
                <ScoreCircle score={stats.score} type={type} />
            </Col>
            <Col md={7}>
                <Row>
                    {labels.map((label, i) => {
                        const key = `${label}_count`;
                        const count = stats[key] || 0;
                        const percentage = (
                            (count / (total || 1)) *
                            100
                        ).toFixed(2);

                        return (
                            <Col md={4} key={label}>
                                <Card
                                    bodyClass={`bg-light-${colors[i]} rounded-bottom`}
                                    containerClass={`text-center h-100 stats-${colors[i]} border-${colors[i]} rounded`}
                                    header={
                                        <h2
                                            className={`bg-${colors[i]} text-white mb-0 p-1`}>
                                            {t(uppercase(label))}
                                        </h2>
                                    }>
                                    <>
                                        <h3 className={`text-${colors[i]}`}>
                                            {count}
                                        </h3>
                                        <h3 className="text-muted">
                                            ({percentage}%)
                                        </h3>
                                    </>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </Col>
        </Row>
    );
};

// Survey feedback with avatars and ratings
const SurveyFeedback = ({ responses, surveyType }) => {
    const getLabelAndClass = rating => {
        if (surveyType === "csat") {
            if (rating >= 4) return ["Satisfied", "success"];
            if (rating === 3) return ["Neutral", "warning"];
            return ["Unsatisfied", "danger", "rating-unsatisfied"];
        } else {
            if (rating >= 9) return ["Promoter", "success"];
            if (rating >= 7) return ["Passive", "warning"];
            return ["Detractor", "danger"];
        }
    };
    const { t } = useTranslation();

    return (
        <>
            <div className="position-relative ps-5 ms-3">
                <div
                    className="position-absolute top-0 bottom-0 start-50 translate-middle-x bg-secondary"
                    style={{ width: 2, zIndex: 0 }}></div>

                <ul className="list-unstyled position-relative">
                    {responses.map((item, index) => {
                        const [label, badgeColor] = getLabelAndClass(
                            item.rating
                        );
                        return (
                            <li className="mb-4 position-relative" key={index}>
                                <div
                                    className={`position-absolute start-0 translate-middle bg-white border border-${badgeColor} rounded-circle d-flex align-items-center justify-content-center shadow`}
                                    style={{
                                        width     : 40,
                                        height    : 40,
                                        fontSize  : 18,
                                        color     : "",
                                        fontWeight: "bold",
                                        top       : "34px"
                                    }}>
                                    {item.rating}
                                </div>

                                <div className="ms-5 p-3 bg-white border rounded shadow-sm">
                                    <div className="d-flex align-items-center mb-2 flex-wrap gap-2 font-medium-2">
                                        <span className="fw-medium ">
                                            {t(item.people_name)}
                                        </span>
                                        <small className="text-muted">
                                            ({item.people_email})
                                        </small>
                                        <span
                                            className={`badge bg-${badgeColor} text-white ms-auto rounded-pill px-3`}>
                                            {" "}
                                            {t(label)} - {item.rating}/
                                            {surveyType === "csat" ? 5 : 10}
                                        </span>
                                    </div>
                                    {item.additional_comment ? (
                                        <div className="border-top mt-1 pt-1">
                                            <div className="p-3 bg-light rounded text-muted fst-italic small font-medium-2">
                                                {t(item.additional_comment)}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </>
    );
};

// Main Survey Dashboard
const SurveyResponse = () => {
    const { data, loading } = useResponse();
    const { t } = useTranslation();
    const { platformId } = useParams();
    const renderStats = () => {
        if (!data) return null;
        const { survey_type, csat_stats, promoter_stats } = data;

        if (survey_type === "csat" && csat_stats) {
            return <SurveyStats type={survey_type} stats={csat_stats} />;
        }
        if (survey_type === "nps" && promoter_stats) {
            return <SurveyStats type={survey_type} stats={promoter_stats} />;
        }
        return null;
    };

    return (
        <>
            <Breadcrumbs
                items={[
                    {
                        title: "Surveys",
                        to   : `/product/${platformId}/surveys`
                    },
                    {
                        title : data.survey_name || "-",
                        active: true
                    }
                ]}/>
            <LoaderOverlay
                active={loading}
                children={
                    <Container className="py-4">
                        <Card containerClass="shadow-sm rounded">
                            {size(data) ? (
                                <>
                                    <div className="text-center mb-4">
                                        <h2 className="mt-2">
                                            {data?.survey_name}
                                        </h2>
                                        <h3 className="text-muted">
                                            {data?.total_responses}{" "}
                                            {getPlural({ text: "Response", length: data?.total_responses })}{" "}
                                                ·{" "}
                                            {data?.survey_type === "csat" ? t(
                                                "Customer Satisfaction Survey"
                                            ) : t(
                                                "Net Promoter Score Survey"
                                            )}
                                        </h3>
                                    </div>

                                    {renderStats()}

                                    <h3 className="border p-2">
                                        {t("Feedbacks")}
                                    </h3>
                                    {size(data?.responses) ? (
                                        <SurveyFeedback
                                            responses={data.responses}
                                            surveyType={data.survey_type}/>
                                    ) : <NoDataComponent
                                        title="No survey response data available"
                                        desc="The survey data could not be loaded or doesn't exist."/>}
                                </>
                            ) : (
                                <NoDataComponent
                                    title="No survey response data available"
                                    desc="The survey data could not be loaded or doesn't exist."/>
                            )}
                        </Card>
                    </Container>
                }/>
        </>
    );
};

export default SurveyResponse;
